import store from "@/store";
import {
  ZITADEL_ROLE_EMPLOYEE,
  ZITADEL_ROLE_FREELANCER,
  CLIENT_ADMIN,
  CLIENT_PROJECT_MANAGER,
  CLIENT_VIEWER,
} from "@/constants/roles";

// Roles held by someone opening a `?token=` share link. Which one they get is
// decided by the access level baked into that token — see setAnonymousRole.
export const ANONYMOUS = "ANONYMOUS";
export const ANONYMOUS_VIEW_ONLY = "ANONYMOUS_VIEW_ONLY";
export const ANONYMOUS_MODEL_ONLY = "ANONYMOUS_MODEL_ONLY";
export const FRONTEND_ONLY_DEMO = "FRONTEND_ONLY_DEMO";

/**
 * How much of a project a share link exposes. The value travels in the share
 * token's `access_level` claim, so it is signed and cannot be edited by the
 * recipient.
 */
export const ShareAccessLevel = {
  // The 3D model on its own — no saved measurements, solar layouts or images.
  MODEL_ONLY: 1,
  // The model plus everything already planned on it, strictly to look at.
  VIEW_ANNOTATIONS: 2,
  // As above, but the recipient may also draw their own; nothing is saved.
  DRAFT_ANNOTATIONS: 3,
};

export const Permission = {
  View_Objects: "View_Objects",
  // Whether measurements, solar layouts and annotation markers already saved
  // on the project are fetched and drawn at all. Distinct from View_Measure,
  // which is about being handed the tools to add more.
  View_Annotations: "View_Annotations",
  View_PersonalSettings: "View_PersonalSettings",
  Create_Objects: "Create_Objects",
  Update_Objects: "Update_Objects",
  View_Measure: "View_Measure",
  View_Gallery: "View_Gallery",
  View_Solar: "View_Solar",
  // The editor chrome around the canvas: the project sidebar, the mode palette
  // and undo/redo. A model-only share is meant to be the 3D model and nothing
  // else, so ANONYMOUS_MODEL_ONLY is the one role without this.
  View_Tools: "View_Tools",
};

const CLIENT_VIEWER_PERMISSION = [
  Permission.View_Objects,
  Permission.View_Annotations,
  Permission.View_PersonalSettings,
  Permission.View_Measure,
  Permission.View_Solar,
  Permission.View_Tools
];

const FULL_EDIT = [
  ...CLIENT_VIEWER_PERMISSION,
  Permission.Create_Objects,
  Permission.Update_Objects,
  Permission.View_Solar,
  Permission.View_Gallery,
  Permission.View_Measure,
];

export const RolePermissions = {
  [ZITADEL_ROLE_EMPLOYEE]: FULL_EDIT,
  [ZITADEL_ROLE_FREELANCER]: FULL_EDIT,
  [CLIENT_ADMIN]: FULL_EDIT,
  [CLIENT_PROJECT_MANAGER]: FULL_EDIT,
  [CLIENT_VIEWER]: CLIENT_VIEWER_PERMISSION,
  // Level 3. Measuring works but Create_/Update_Objects are absent, so the
  // geometry lives in the recipient's session only and is never persisted.
  [ANONYMOUS]: [
    Permission.View_Objects,
    Permission.View_Annotations,
    Permission.View_Measure,
    Permission.View_Gallery,
    Permission.View_Tools,
  ],
  // Level 2: same view, minus the tools for adding anything. Keeps the sidebar,
  // which is where the existing measurements and solar layouts are listed.
  [ANONYMOUS_VIEW_ONLY]: [
    Permission.View_Objects,
    Permission.View_Annotations,
    Permission.View_Gallery,
    Permission.View_Tools,
  ],
  // Level 1: the bare model, on a bare canvas. Without View_Annotations the
  // viewer never even requests the saved objects, so they are not just hidden
  // but unsent; without View_Tools there is no sidebar, mode palette or
  // undo/redo either.
  [ANONYMOUS_MODEL_ONLY]: [Permission.View_Objects],
  [FRONTEND_ONLY_DEMO]: [
    Permission.View_Objects,
    Permission.View_Annotations,
    Permission.View_Measure,
    Permission.View_Solar,
    Permission.View_Tools,
  ],
};

const ANONYMOUS_ROLE_BY_ACCESS_LEVEL = {
  [ShareAccessLevel.MODEL_ONLY]: ANONYMOUS_MODEL_ONLY,
  [ShareAccessLevel.VIEW_ANNOTATIONS]: ANONYMOUS_VIEW_ONLY,
  [ShareAccessLevel.DRAFT_ANNOTATIONS]: ANONYMOUS,
};

function getActualRoles() {
  const roles = store.getters.roles || [];
  const isEmployee = store.getters.activeUserIsEmployee;
  const isSampleProject = store.getters.isSampleProject;

  // Non-employees on sample projects are demoted to viewer-level (read-only).
  // Employees keep full edit on sample projects.
  if (isSampleProject && !isEmployee) return [CLIENT_VIEWER];

  const actualRoles = isEmployee ? [ZITADEL_ROLE_EMPLOYEE, ...roles] : [...roles];
  if (actualRoles.length === 0) actualRoles.push(ANONYMOUS);
  return actualRoles;
}

export function hasPermission(permission) {
  for (const role of getActualRoles()) {
    const perms = RolePermissions[role];
    if (perms && perms.includes(permission)) return true;
  }
  return false;
}

/**
 * @param {number} [accessLevel] the share token's `access_level` claim.
 *   An absent claim means a link minted before granular sharing existed, so it
 *   keeps the level it was created under (DRAFT_ANNOTATIONS). A claim that is
 *   present but unrecognised should not happen — the token is signed — so it
 *   is treated as a fault and given the least access rather than the most.
 */
export function setAnonymousRole(accessLevel) {
  if (accessLevel === undefined || accessLevel === null) {
    store.commit("setRoles", [ANONYMOUS]);
    return;
  }
  const role = ANONYMOUS_ROLE_BY_ACCESS_LEVEL[accessLevel] ?? ANONYMOUS_MODEL_ONLY;
  store.commit("setRoles", [role]);
}

export function setFrontendOnlyDemoRole() {
  store.commit("setRoles", [FRONTEND_ONLY_DEMO]);
}
